/* ============================================================================
   NAVIGATE — bấm vào một ô thì nhân vật TỰ ĐI tới đó rồi mới làm việc.

   Đây là một CÁCH NHẬP LIỆU, không phải luật chơi: nó chỉ sinh ra vector di
   chuyển từng khung hình, y hệt như bàn phím hay joystick. Toàn bộ thay đổi
   state vẫn đi qua action MOVE/USE như cũ, nên `src/game/` không phải biết gì
   về nó và định dạng save cũng không đổi. Đích đến là ý định nhất thời của
   người chơi — không thuộc về game state, không đáng lưu vào file save.

   Ba việc:

   1. **Tìm đường A*** trên lưới ô, 8 hướng. Đi thẳng tới đích sẽ kẹt cứng ở
      góc nhà hay gốc cây; bản đồ chỉ 1200 ô nên A* rẻ như không.
   2. **Kéo dây (string pulling).** Đường A* đi theo tâm từng ô nên trông rất
      máy móc. Mỗi khung hình ta thử bỏ qua các điểm mốc còn nhìn thấy được
      đích xa hơn, nên nhân vật đi theo đường chéo tự nhiên.
   3. **Dừng khi ĐỦ GẦN, không phải khi giẫm lên đích.** Người chơi bấm vào ô
      đất là muốn cày nó, chứ không phải muốn đứng lên nó — nên chỉ cần vào
      trong khoảng một ô là dừng và xử lý.
============================================================================ */

import type { Content, GameState } from "../game/types.ts";
import { findPath, lineOfSightBox, walkableTile } from "../game/pathfind.ts";
import {
  REACH_TILES,
  TILE,
  distToTile,
  idx,
  tileCenterX,
  tileCenterY,
} from "../game/world.ts";

/** Tầm coi là "đã tới nơi" khi đi để LÀM VIỆC.
 *
 *  1,05 ô: vừa đủ chứa ô kề thẳng khi đứng đúng TÂM ô đó (1,0), nhưng không
 *  chứa ô kề chéo (1,41). Đặt rộng hơn (1,2) thì nhân vật dừng lùi lại vài pixel
 *  so với tâm ô, nhìn cứ như chưa tiến hẳn vào lô đất.
 *
 *  Nếu vì vật cản mà không bao giờ vào sát được thì `finishOrGiveUp` vẫn cứu:
 *  đi hết đường mà đã lọt tầm với thì cứ làm. */
const ARRIVE_TILES = 1.05;

/** Đứng cách tâm ô đích quá ngần này (world px) trên trục thẳng hàng thì coi
 *  như chưa thẳng hàng. Đứng đúng tâm ô kề thì lệch bằng 0. */
const ALIGN_SLACK = 4;

/** Đi THUẦN TUÝ (bấm bản đồ nhỏ) thì mới tính là tới khi đã giẫm lên ô đó. */
const TRAVEL_ARRIVE_TILES = 0.6;

/**
 * Nhân vật đã đứng THẲNG HÀNG với ô đích chưa — cùng cột hoặc cùng hàng, và
 * sát bên.
 *
 * Đây là điểm khác biệt so với chỉ đo khoảng cách: đứng chéo góc cách ô đất
 * 1,41 ô thì vẫn "với tới" được, nhưng nhìn rất lệch và tư thế vung tay chỉ
 * sang hướng chẳng liên quan. Người chơi mong nhân vật tiến vào cho ngang bằng
 * với lô đất rồi mới làm.
 */
export function alignedTo(state: GameState, tx: number, ty: number): boolean {
  const dx = Math.abs(state.player.x - tileCenterX(tx));
  const dy = Math.abs(state.player.y - tileCenterY(ty));
  if (Math.hypot(dx, dy) > ARRIVE_TILES * TILE) return false;

  // Phải ĐỨNG HẲN trên ô đích hoặc một ô kề THẲNG của nó. Chỉ đo khoảng cách là
  // chưa đủ: đứng vắt ở góc hai ô vẫn có thể lọt ngưỡng mà nhìn thì vẫn xiên.
  const ptx = Math.floor(state.player.x / TILE);
  const pty = Math.floor(state.player.y / TILE);
  const onTileLine =
    (ptx === tx && Math.abs(pty - ty) <= 1) || (pty === ty && Math.abs(ptx - tx) <= 1);
  if (!onTileLine) return false;

  return dx <= ALIGN_SLACK || dy <= ALIGN_SLACK;
}

/** Bỏ cuộc nếu đi mãi không tiến thêm được (bị kẹt sau lưng vật cản). */
const STUCK_SECONDS = 0.6;
const STUCK_DISTANCE = 3;

/** Trần số ô A* được phép mở, chặn trường hợp đích bị vây kín. */

export interface NavTarget {
  tx: number;
  ty: number;
  /** Tới nơi thì có làm việc gì không. Bấm lên bản đồ nhỏ = ĐI THUẦN TUÝ, không
   *  cày cuốc gì cả — nếu không thì đang cầm cuốc mà bấm minimap là tự cày. */
  act: boolean;
}

export interface NavOptions {
  /** Cầm công trình đặc thì phải đứng CẠNH ô, không đứng lên nó. */
  avoidStandingOn?: boolean;
  /** false = chỉ đi tới, không thao tác. */
  act?: boolean;
}

export interface Navigator {
  /** Đặt đích mới. Trả về false nếu không có đường đi (đích bị vây kín). */
  goTo(state: GameState, content: Content, tx: number, ty: number, opts?: NavOptions): boolean;
  cancel(): void;
  isActive(): boolean;
  target(): NavTarget | null;
  /** Gọi mỗi khung hình. Trả vector đi, hoặc null khi không có đích. */
  update(state: GameState, content: Content, dt: number): { dx: number; dy: number; run: boolean } | null;
  /** true đúng MỘT lần, ngay khung hình nhân vật vào đủ gần đích. */
  takeArrival(): NavTarget | null;
}

/* -------------------------------------------------------------------------- */


/* -------------------------------------------------------------------------- */

export function createNavigator(): Navigator {
  let path: NavTarget[] = [];
  let goal: NavTarget | null = null;
  let arrived: NavTarget | null = null;
  let stuckTime = 0;
  let lastX = 0;
  let lastY = 0;

  const clear = () => {
    path = [];
    goal = null;
    stuckTime = 0;
  };

  /** Hết điểm mốc rồi. Nếu đã lọt vào TẦM VỚI THẬT của game thì coi như tới nơi
   *  và xử lý luôn — không thì mới bỏ cuộc.
   *
   *  Cần cả hai ngưỡng: ARRIVE_TILES để DỪNG SỚM ngay khi đủ gần (khỏi đi thừa),
   *  còn REACH_TILES là mốc chốt lúc đi hết đường. Đi tới ô kề chéo cho khoảng
   *  cách 1,41 ô, cộng vài pixel sai số là vượt 1,5 — chỉ có một ngưỡng thì
   *  nhân vật đi tới nơi rồi đứng ngẩn ra không làm gì. */
  const finishOrGiveUp = (state: GameState) => {
    if (goal && distToTile(state, goal.tx, goal.ty) <= REACH_TILES) arrived = goal;
    clear();
  };

  return {
    goTo(state, content, tx, ty, opts = {}) {
      const avoidStandingOn = opts.avoidStandingOn === true;
      const act = opts.act !== false;
      clear();
      if (tx < 0 || ty < 0 || tx >= state.w || ty >= state.h) return false;

      // Đã đứng đúng chỗ rồi thì khỏi đi đâu cả — người gọi tự xử lý ngay.
      if (act ? alignedTo(state, tx, ty) : distToTile(state, tx, ty) <= TRAVEL_ARRIVE_TILES)
        return false;

      const at = (x: number, y: number) => idx(state.w, x, y);
      const ORTHO: [number, number][] = [[0, -1], [0, 1], [-1, 0], [1, 0]];
      const DIAG: [number, number][] = [[-1, -1], [1, -1], [-1, 1], [1, 1]];

      // Thứ tự ưu tiên chỗ đứng:
      //   1. bốn ô kề THẲNG  → thẳng hàng với lô đất, tư thế vung tay đúng hướng
      //   2. chính ô đó       → chỉ khi đi thuần tuý, hoặc không cấm giẫm lên
      //   3. bốn ô kề CHÉO    → phương án chót khi ô đất bị kẹp giữa vật cản
      // Chạy A* theo từng nhóm thay vì gộp một tập: A* với tập gộp sẽ vớ lấy ô
      // nào GẦN NHẤT, mà ô chéo thường gần hơn ô thẳng — đúng cái ta muốn tránh.
      const groups: number[][] = [];
      const ortho = ORTHO.filter(([dx, dy]) => walkableTile(state, content, tx + dx, ty + dy)).map(
        ([dx, dy]) => at(tx + dx, ty + dy),
      );
      if (ortho.length) groups.push(ortho);

      const selfOk = !avoidStandingOn && walkableTile(state, content, tx, ty);
      // Khi đi thuần tuý thì đích là chính ô đó, nên nó phải được xét TRƯỚC.
      if (selfOk) groups[act ? "push" : "unshift"]([at(tx, ty)]);

      const diag = DIAG.filter(([dx, dy]) => walkableTile(state, content, tx + dx, ty + dy)).map(
        ([dx, dy]) => at(tx + dx, ty + dy),
      );
      if (diag.length) groups.push(diag);
      if (!groups.length) return false;

      const px = Math.floor(state.player.x / TILE);
      const py = Math.floor(state.player.y / TILE);
      let found: number[] | null = null;
      for (const gset of groups) {
        found = findPath(state, content, px, py, new Set(gset));
        if (found) break;
      }
      if (!found) return false;

      // `findPath` trả CHỈ SỐ Ô (định dạng gọn để lưu được vào save của thực
      // thể); người chơi thì vẫn dùng danh sách điểm mốc như cũ.
      path = found.map((i) => ({ tx: i % state.w, ty: (i / state.w) | 0, act: false }));
      goal = { tx, ty, act };
      lastX = state.player.x;
      lastY = state.player.y;
      stuckTime = 0;
      return true;
    },

    cancel: clear,
    isActive: () => goal !== null,
    target: () => goal,

    takeArrival() {
      const a = arrived;
      arrived = null;
      return a;
    },

    update(state, content, dt) {
      if (!goal) return null;

      // Tới nơi? Kiểm TRƯỚC khi đi tiếp.
      //
      // Đi để LÀM VIỆC thì phải THẲNG HÀNG với ô đất mới dừng — chỉ "gần" thôi
      // là chưa đủ, vì đứng chéo góc vẫn với tới được nhưng nhìn lệch hẳn.
      // Đi thuần tuý (bản đồ nhỏ) thì giẫm lên ô đó mới là tới.
      const done = goal.act
        ? alignedTo(state, goal.tx, goal.ty)
        : distToTile(state, goal.tx, goal.ty) <= TRAVEL_ARRIVE_TILES;
      if (done) {
        arrived = goal;
        clear();
        return null;
      }

      const px = state.player.x;
      const py = state.player.y;

      // Kéo dây: bỏ qua mọi điểm mốc mà từ đây vẫn nhìn thẳng tới được. Nhờ vậy
      // nhân vật cắt chéo qua bãi trống thay vì đi zigzag theo tâm từng ô.
      let skipped = 0;
      while (path.length > 1 && skipped < 8) {
        const next = path[1]!;
        if (!lineOfSightBox(state, content, px, py, tileCenterX(next.tx), tileCenterY(next.ty))) break;
        path.shift();
        skipped++;
      }

      const wp = path[0];
      if (!wp) {
        finishOrGiveUp(state);
        return null;
      }

      const tx = tileCenterX(wp.tx);
      const ty = tileCenterY(wp.ty);
      const dx = tx - px;
      const dy = ty - py;
      const len = Math.hypot(dx, dy);
      if (len < 2.5) {
        path.shift();
        if (!path.length) {
          finishOrGiveUp(state);
          return null;
        }
        return { dx: 0, dy: 0, run: false };
      }

      // Kẹt: đi mãi mà không nhích được thì bỏ cuộc, còn hơn dí vào tường mãi.
      if (Math.hypot(px - lastX, py - lastY) < STUCK_DISTANCE) {
        stuckTime += dt;
        if (stuckTime > STUCK_SECONDS) {
          // Kẹt sau lưng vật cản — nhưng nếu đã lọt vào tầm với thì cứ làm.
          finishOrGiveUp(state);
          return null;
        }
      } else {
        stuckTime = 0;
        lastX = px;
        lastY = py;
      }

      // Còn xa thì CHẠY. Đi bộ hết chiều dài bản đồ là cực hình; chạy khi còn
      // trên hai ô làm quãng đường dài ngắn lại hẳn mà bước cuối vẫn đi bộ nên
      // không bị trượt quá đích.
      const remain = distToTile(state, goal.tx, goal.ty);
      return { dx: dx / len, dy: dy / len, run: remain > 2.5 };
    },
  };
}
